import { Button, Group, Modal, ScrollArea, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core';
import { useState } from 'react';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { primaryColor } from '../../constants/theme';
import { useApp } from '../../context/AppContext';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface DatePickerModalProps {
  opened: boolean;
  label: string;
  initialDate: string;
  onClose: () => void;
  onConfirm: (date: string) => void;
  enabledDates?: Set<string>;
}

const buildMonths = (): Date[] =>
  Array.from({ length: 12 }, (_, i) => addMonths(startOfMonth(new Date()), i));

const buildDays = (month: Date): Date[] => {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days: Date[] = [];
  let cursor = gridStart;
  while (cursor <= gridEnd) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return days;
};

export const DatePickerModal = ({
  opened,
  label,
  initialDate,
  onClose,
  onConfirm,
  enabledDates,
}: DatePickerModalProps) => {
  const months = buildMonths();

  const [selectedMonth, setSelectedMonth] = useState<Date>(() => {
    const parsed = new Date(initialDate);
    return isNaN(parsed.getTime()) ? months[0] : startOfMonth(parsed);
  });

  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const parsed = new Date(initialDate);
    return isNaN(parsed.getTime()) ? '' : format(parsed, 'yyyy-MM-dd');
  });

  const today = startOfDay(new Date());
  const days = buildDays(selectedMonth);
  const { isTabletAndBelow } = useApp();

  const monthHasAvailability = (month: Date): boolean => {
    if (!enabledDates) return true;
    const prefix = format(month, 'yyyy-MM');
    return [...enabledDates].some((d) => d.startsWith(prefix));
  };

  const handleConfirm = () => {
    if (selectedDate) {
      onConfirm(format(new Date(selectedDate), 'dd MMM yyyy'));
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      padding="lg"
      title={
        <Text fw={600} size="h4">
          {label}{' '}
          {selectedDate && (
            <Text component="span" c={primaryColor} fw={600} size="h4">
              {format(new Date(selectedDate), 'dd MMM yyyy')}
            </Text>
          )}
        </Text>
      }
    >
      <Stack gap="md">
        {/* Month picker */}
        {isTabletAndBelow ? (
          <ScrollArea type="auto" offsetScrollbars>
            <Group wrap="nowrap" gap={8} pb={4}>
              {months.map((month) => {
                const isSelected = isSameMonth(month, selectedMonth);
                const isMonthDisabled = !monthHasAvailability(month);
                return (
                  <UnstyledButton
                    key={month.toISOString()}
                    onClick={() => !isMonthDisabled && setSelectedMonth(month)}
                    disabled={isMonthDisabled}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0, opacity: isMonthDisabled ? 0.3 : 1, cursor: isMonthDisabled ? 'default' : 'pointer' }}
                  >
                    <div style={{ border: `1px solid ${isSelected ? primaryColor : '#ced4da'}`, borderRadius: '50%', width: 52, height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isSelected ? primaryColor : 'transparent' }}>
                      <Text size="xs" fw={600} c={isSelected ? 'white' : primaryColor} lh={1}>{format(month, 'MMM')}</Text>
                    </div>
                    <Text size={10} c="dimmed" lh={1}>{format(month, 'yyyy')}</Text>
                  </UnstyledButton>
                );
              })}
            </Group>
          </ScrollArea>
        ) : (
          <SimpleGrid cols={6} spacing={6}>
            {months.map((month) => {
              const isSelected = isSameMonth(month, selectedMonth);
              const isMonthDisabled = !monthHasAvailability(month);
              return (
                <UnstyledButton
                  key={month.toISOString()}
                  onClick={() => !isMonthDisabled && setSelectedMonth(month)}
                  disabled={isMonthDisabled}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, opacity: isMonthDisabled ? 0.3 : 1, cursor: isMonthDisabled ? 'default' : 'pointer' }}
                >
                  <div style={{ border: `1px solid ${isSelected ? primaryColor : '#ced4da'}`, borderRadius: '50%', width: 52, height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isSelected ? primaryColor : 'transparent' }}>
                    <Text size="xs" fw={600} c={isSelected ? 'white' : primaryColor} lh={1}>{format(month, 'MMM')}</Text>
                  </div>
                  <Text size={10} c="dimmed" lh={1}>{format(month, 'yyyy')}</Text>
                </UnstyledButton>
              );
            })}
          </SimpleGrid>
        )}

        {/* Day-of-week headers */}
        <SimpleGrid cols={7} spacing={4} mt="md">
          {DAY_LABELS.map((day) => (
            <Text key={day} ta="center" size="xs" c="dimmed" fw={500}>
              {day}
            </Text>
          ))}

          {/* Date buttons */}
          {days.map((day) => {
            const isoDate = format(day, 'yyyy-MM-dd');
            const inMonth = isSameMonth(day, selectedMonth);
            const isPast = isBefore(startOfDay(day), today);
            const isDisabled = enabledDates
              ? !enabledDates.has(isoDate)
              : isPast || !inMonth;
            const isSelected = isoDate === selectedDate;

            return (
              <UnstyledButton
                key={isoDate}
                disabled={isDisabled}
                onClick={() => !isDisabled && setSelectedDate(isoDate)}
                style={(theme) => ({
                  border: inMonth
                    ? `1px solid ${isSelected ? theme.colors[primaryColor]?.[6] ?? primaryColor : theme.colors.gray[3]}`
                    : 'none',
                  borderRadius: theme.radius.sm,
                  aspectRatio: '1',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  background: isSelected ? (theme.colors[primaryColor]?.[6] ?? primaryColor) : 'transparent',
                  opacity: isDisabled ? 0.3 : 1,
                  cursor: isDisabled ? 'default' : 'pointer',
                  minHeight: 36,
                })}
              >
                <Text
                  size="xs"
                  fw={isSelected ? 700 : 400}
                  c={isSelected ? 'white' : inMonth ? 'dark' : 'dimmed'}
                >
                  {inMonth ? format(day, 'd') : ''}
                </Text>
              </UnstyledButton>
            );
          })}
        </SimpleGrid>

        <Button
          color={primaryColor}
          fullWidth
          disabled={!selectedDate}
          onClick={handleConfirm}
        >
          Set
        </Button>
      </Stack>
    </Modal>
  );
};
